import React from "react";
// router
import { useNavigate } from "react-router";
// mui
import { Container, Stack, Typography } from "@mui/material";
// components
import UploadImage from "../../components/form-components/UploadImage";
import InputField from "../../components/form-components/InputField";
import SelectSearch from "../../components/form-components/SelectSearch";
import SubmitButton from "../../components/form-components/SubmitButton";
import IntlPhoneInput from "../../components/phone-input/IntlPhoneInput";
// form
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { joinBusinessSchema } from "../../schemes/join-business/joinBusinessSchema";
// fetching
import fetching from "../../helpers/fetching/fetching";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// helpers
import { fetchingError } from "../../helpers/fetching/fetchingError";
import fireAlert from "../../helpers/alerts/fireAlert";
import { valuesToFormData } from "../../helpers/fetching/valuesToFormData";
// types
import { Category } from "../../types/types";

const JoinNewBusinessForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // fetching
  const getCities = () => {
    return fetching(`/cities?all`, undefined)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          throw data;
        }
        return data;
      });
  };

  const getMainCategories = () => {
    return fetching(
      `/categories?filters=super_parent_id:${process.env.REACT_APP_BUSNIESS_PARENT_CATEGORY_ID},depth:1&all`,
      undefined
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          throw data;
        }
        return data;
      });
  };
  const {
    data: cities,
    isLoading: isLoadingCities,
    isError: isErrorCities,
    error: errorCities,
  } = useQuery({
    queryKey: ["cities"],
    queryFn: getCities,
  });

  const {
    data: categories,
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
    error: errorCategories,
  } = useQuery<{ meta: Record<string, any>; data: Category[] }, Error>({
    queryKey: ["categories"],
    queryFn: getMainCategories,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    trigger,
    watch,
    control,
  } = useForm({
    defaultValues: {
      img: undefined,
      title: "",
      owner_name: "",
      phone: "",
      city: {
        id: "",
        name: "",
      },
      parent_category: { id: "", name: "" },
    },
    resolver: yupResolver(joinBusinessSchema),
  });

  // form submitting
  const addBusiness = (formData: FormData): Promise<any> => {
    return fetching(`/business-forms`, undefined, "POST", formData)
      .then((res) => res.json())
      .then((data) => {
        if (!data.id) {
          throw data;
        }
        return data;
      });
  };

  const { mutate, error, isPending } = useMutation({
    mutationFn: addBusiness,
    onSuccess: () => {
      fireAlert("تم اضافة النشاط بنجاح");
      navigate("/join/success");
      queryClient.invalidateQueries({
        queryKey: ["business"],
      });
    },
    onError(error, variables, context) {
      fetchingError(error, setError);
    },
  });

  const submitting: SubmitHandler<any> = (values) => {
    const formData: FormData = valuesToFormData(values);
    mutate(formData);
  };

  return (
    <Stack py={3}>
      <Container>
        <Stack
          component="form"
          onSubmit={handleSubmit(submitting)}
          gap="16px"
          width="100%"
          maxWidth="700px"
          marginX="auto"
        >
          <Typography
            variant="h3"
            sx={{ color: "#111", mb: 3, textAlign: "center" }}
          >
            طلب انضمام الي الانشطة
          </Typography>
          <UploadImage
            setValue={setValue}
            trigger={trigger}
            errorMessage={errors?.img?.message}
            height="200px"
            imgAspectRatio="16/9"
            objectFit="cover"
            watch={watch}
          />
          <InputField
            register={{ ...register("title") }}
            name="title"
            label="اسم النشاط"
            type="text"
            errorMessage={errors?.title?.message}
          />
          <InputField
            register={{ ...register("owner_name") }}
            name="owner_name"
            label="اسم صاحب النشاط"
            type="text"
            errorMessage={errors?.owner_name?.message}
          />
          <InputField
            register={{ ...register("phone") }}
            name="phone"
            label="رقم الهاتف"
            type="tel"
            errorMessage={errors?.phone?.message}
            placeholder="+21891xxxxxxxx"
          />
          {/* <IntlPhoneInput
            control={control}
            name="phone"
            label="رقم الهاتف"
            errorMessage={errors?.phone?.message}
          /> */}
          <SelectSearch
            label="المدينة"
            options={cities?.data || []}
            loading={isLoadingCities}
            getOptionLabel={(option) => option.name || ""}
            onChange={(e, newValue) => {
              setValue("city", { id: newValue?.id, name: newValue?.name });
            }}
            value={watch("city")}
            errorMsg={errors?.city?.id?.message}
          />
          <SelectSearch
            label="التصنيف الاساسي"
            options={categories?.data?.length ? categories.data : []}
            loading={isLoadingCategories}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            errorMsg={errors?.parent_category?.message?.toString()}
            onChange={(e, newValue) => {
              setValue("parent_category", newValue);
            }}
            value={watch("parent_category")}
            disabled={isLoadingCategories}
            sx={{ flex: "1" }}
          />
          <SubmitButton label="ارسال" disabled={isPending} />
        </Stack>
      </Container>
    </Stack>
  );
};

export default JoinNewBusinessForm;
